import { CLOSE_LOADER, OPEN_LOADER } from "../store/Loader/loader.type";
import { baseURL, secretKey } from "./config";
import store from "../store/Provider";
import { notifyPermissionDenied } from "./ModuleAccessContext";
import {
  apiHeaders,
  canRefresh,
  ensureFreshSession,
  forceLogin,
  isSessionError,
  isSignedIn,
} from "./authSession";

export const openSpinner = () => {
  return store.dispatch({ type: OPEN_LOADER });
};

export const closeSpinner = () => {
  return store.dispatch({ type: CLOSE_LOADER });
};

// Helper function to get latest headers. The session is the HttpOnly cookie
// (sent via `credentials: "include"`); the client header lets the API read it.
const getUpdatedHeaders = () =>
  apiHeaders({
    "Content-Type": "application/json",
    key: secretKey, // Will always use the latest secretKey
  });

const getBaseURL = () => {
  const url = baseURL;
  return url.endsWith("/") ? url : `${url}/`;
};

/**
 * Hard ceiling on every request.
 *
 * `fetch` has no timeout of its own, so a request that never settles (server
 * restarting, connection stalled, a reload racing an in-flight call) leaves the
 * global loader Dialog open forever — and that Dialog has `disableEscapeKeyDown`,
 * so the whole panel becomes unusable with no way out. Aborting guarantees the
 * `.finally` below always runs.
 */
const REQUEST_TIMEOUT_MS = 30000;

/** Run a fetch with an abort timer, clearing it however the request ends. */
const withTimeout = (run) => {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);

  return run(controller.signal)
    .catch((error) => {
      // A timed-out fetch rejects with a bare `AbortError` ("The user aborted a
      // request"), which is both wrong and meaningless to whoever sees the
      // toast. Replace it with what actually happened.
      if (timedOut) {
        const timeoutError = new Error(
          "The server took too long to respond. Please try again."
        );
        timeoutError.status = 408;
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      throw error;
    })
    .finally(() => clearTimeout(timer));
};

const sessionErrorIn = async (response) => {
  if (response.status !== 401) return false;
  try {
    const data = await response.clone().json();
    return isSessionError(response.status, data?.code);
  } catch (_) {
    return false;
  }
};

/**
 * Send with the session cookie. When the session cookie is missing / expired,
 * refresh once (shared with every other request failing at the same time) and
 * retry once; if the retry is still refused the session is over.
 */
async function fetchAuthorized(url, buildInit) {
  const withCookies = () => ({ ...buildInit(), credentials: "include" });
  const sentAt = Date.now();
  const response = await fetch(url, withCookies());
  if (!(await sessionErrorIn(response)) || !canRefresh()) return response;
  try {
    await ensureFreshSession(sentAt);
  } catch (_) {
    return response;
  }
  const retried = await fetch(url, withCookies());
  if (isSignedIn() && (await sessionErrorIn(retried))) forceLogin();
  return retried;
}

export const apiInstanceFetch = {
  get baseURL() {
    return getBaseURL();
  },
  get headers() {
    return getUpdatedHeaders();
  },
  get: (url, showSpinner = true) => {
    if (showSpinner) openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "GET",
        headers: getUpdatedHeaders(),
        // Avoid 304 responses with empty bodies breaking JSON parsing
        cache: "no-store",
        signal,
      }))
        .then(handleErrors)
        .finally(() => {
          // Only close what this call opened. Closing unconditionally let a
          // `get(url, false)` dismiss the spinner a *different* request was
          // still legitimately showing.
          if (!showSpinner) return;
          if (url && typeof url === 'string' && url.includes('city?regionId='))
            setTimeout(() => {
              closeSpinner()
            }, 800);
          else
            closeSpinner()
        })
    );
  },
  getFile: (url, showSpinner = true) => {
    if (showSpinner) openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "GET",
        headers: getUpdatedHeaders(),
        cache: "no-store",
        signal,
      }))
        .then(handleFileResponse)
        .finally(() => {
          if (showSpinner) closeSpinner();
        })
    );
  },

  post: (url, data) => {
    openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "POST",
        headers: getUpdatedHeaders(),
        body: JSON.stringify(data),
        signal,
      }))
        .then(handleErrors)
        .finally(() => closeSpinner())
    );
  },

  // Deliberately NOT wrapped in withTimeout: this is the file-upload path (KYC
  // sends ID images and a video), and a large upload on a slow connection can
  // legitimately run well past the 30s ceiling. Aborting it would turn a working
  // upload into a failure. The loader watchdog in Pages/Loader.js is what stops
  // a stalled upload from locking the UI.
  postFormData: (url, formData, showSpinner = true) => {
    if (showSpinner) openSpinner();
    return fetchAuthorized(`${getBaseURL()}${url}`, () => {
      const retryHeaders = getUpdatedHeaders();
      delete retryHeaders["Content-Type"];
      return {
        method: "POST",
        headers: retryHeaders,
        body: formData,
      };
    })
      .then(handleErrors)
      .finally(() => {
        if (showSpinner) closeSpinner();
      });
  },

  patch: (url, data) => {
    openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "PATCH",
        headers: getUpdatedHeaders(),
        body: JSON.stringify(data),
        signal,
      }))
        .then(handleErrors)
        .finally(() => closeSpinner())
    );
  },

  put: (url, data) => {
    openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "PUT",
        headers: getUpdatedHeaders(),
        body: JSON.stringify(data),
        signal,
      }))
        .then(handleErrors)
        .finally(() => closeSpinner())
    );
  },

  delete: (url) => {
    openSpinner();
    return withTimeout((signal) =>
      fetchAuthorized(`${getBaseURL()}${url}`, () => ({
        method: "DELETE",
        headers: getUpdatedHeaders(),
        signal,
      }))
        .then(handleErrors)
        .finally(() => closeSpinner())
    );
  },
};

async function handleErrors(response) {
  if (!response.ok) {
    // Surface the server's JSON error message (e.g. validation / 409 conflicts)
    // instead of a generic "HTTP error! Status: undefined".
    let message = `Request failed (status ${response.status}).`;
    try {
      const text = await response.text();
      if (text) {
        const data = JSON.parse(text);
        if (data && data.message) message = data.message;
        // A sub-admin tried to edit or delete where they may only view.
        if (
          response.status === 403 &&
          data?.code === "INSUFFICIENT_PERMISSIONS" &&
          data?.requiredAction &&
          data.requiredAction !== "view"
        ) {
          notifyPermissionDenied(message);
        }
      }
    } catch {
      /* non-JSON body — keep the generic status message */
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  // 304/204 typically has no body; treat as empty JSON.
  if (response.status === 304 || response.status === 204) return {};

  // Some APIs may respond with empty body (or non-JSON) even on 200.
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

async function handleFileResponse(response) {
  if (!response.ok) {
    const text = await response.text();
    let message = `HTTP error! Status: ${response.status}`;
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } catch {
        message = text.slice(0, 200) || message;
      }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  return {
    blob,
    contentDisposition: response.headers.get("content-disposition") || "",
    contentType: response.headers.get("content-type") || "",
    payoutExcelSummaryId: response.headers.get("x-payout-excel-summary-id") || "",
    payoutPeriod: response.headers.get("x-payout-period") || "",
    payoutRecordsCount: response.headers.get("x-payout-records-count") || "",
    coveragePercent: response.headers.get("x-payout-coverage-percent") || "",
    isPartialCoverage: response.headers.get("x-payout-is-partial-coverage") === "true",
    effectivePeriodStart: response.headers.get("x-payout-effective-period-start") || "",
    effectivePeriodEnd: response.headers.get("x-payout-effective-period-end") || "",
    coverageMessage: (() => {
      const raw = response.headers.get("x-payout-coverage-message") || "";
      if (!raw) return "";
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    })(),
    finalTransferBatchId: response.headers.get("x-final-transfer-batch-id") || "",
    finalTransferRecordsCount: response.headers.get("x-final-transfer-records-count") || "",
  };
}
